import { ReactNode } from 'react';
import {
  Smile,
  Image as ImageIcon,
  Link,
  Braces,
  TicketPercent,
  ContactRound,
  AlertOctagon
} from 'lucide-react';
import Picker from '@emoji-mart/react';
import data from '@emoji-mart/data';
import { ToolbarContactDialog } from '../contact-card/toolbar-contact-dialog';
import { ContactCardData, ContactCardElement } from '../plugins/with-contact-cards';

export interface ToolbarAction {
  type: 'media' | 'emoji' | 'variable' | 'link' | 'discount' | 'contact' | 'alert';
  icon: ReactNode;
  label: string;
  isDisabled?: boolean;
  isOpen?: boolean;
  onToggle?: () => void;
  onSelect?: (data?: any) => void;
  popoverContent?: ReactNode;
}

interface ToolbarHandlers {
  onImageClick: () => void;
  onEmojiSelect: (emoji: any) => void;
  onAlertClick: () => void;
  onVariableClick: () => void;
  onShortLinkClick: () => void;
  hasImage: boolean;
  isEmojiOpen: boolean;
  togglePicker: () => void;
  isContactOpen: boolean;
  toggleContact: () => void;
  getContactCards: () => ContactCardData[];
  setShowContactCardDialog: (open: boolean) => void;
  setSelectedContactCard: (card: ContactCardElement | null) => void;
  handleContactCardInsert: (contact: ContactCardData) => void;
}

/**
 * Creates toolbar action groups with proper handlers.
 * Groups actions logically by their purpose:
 * - Media Group: Emoji and image/GIF insertion
 * - Embed Group: Links, variables, and discount codes
 * - Utility Group: Contact info and message info
 */
export function createToolbarActions({
  onImageClick,
  onEmojiSelect,
  onAlertClick,
  onVariableClick,
  onShortLinkClick,
  hasImage,
  isEmojiOpen,
  togglePicker,
  isContactOpen,
  toggleContact,
  getContactCards,
  setShowContactCardDialog,
  setSelectedContactCard,
  handleContactCardInsert
}: ToolbarHandlers): ToolbarAction[][] {
  const mediaGroup: ToolbarAction[] = [
    {
      type: 'emoji',
      icon: <Smile className="h-5 w-5" />,
      label: 'Insert emoji',
      isOpen: isEmojiOpen,
      onToggle: togglePicker,
      popoverContent: (
        <Picker
          data={data}
          onEmojiSelect={onEmojiSelect}
          theme="light"
          previewPosition="none"
          skinTonePosition="none"
          autoFocus
          navPosition="bottom"
        />
      )
    },
    {
      type: 'media',
      icon: <ImageIcon className="h-5 w-5" />,
      label: 'Add image or GIF',
      isDisabled: hasImage,
      onSelect: onImageClick
    }
  ];

  const embedGroup: ToolbarAction[] = [
    {
      type: 'link',
      icon: <Link className="h-5 w-5" />,
      label: 'Insert link',
      onSelect: onShortLinkClick
    },
    {
      type: 'variable',
      icon: <Braces className="h-5 w-5" />,
      label: 'Insert variable',
      onSelect: onVariableClick
    },
    {
      type: 'discount',
      icon: <TicketPercent className="h-5 w-5" />,
      label: 'Insert discount code',
      onSelect: () => {
        console.log('Discount clicked');
      }
    }
  ];

  const utilityGroup: ToolbarAction[] = [
    {
      type: 'contact',
      icon: <ContactRound className="h-5 w-5" />,
      label: 'Insert contact info',
      isOpen: isContactOpen,
      onToggle: toggleContact,
      popoverContent: (
        <ToolbarContactDialog
          open={isContactOpen}
          onOpenChange={toggleContact}
          getContactCards={getContactCards}
          setShowContactCardDialog={setShowContactCardDialog}
          setSelectedContactCard={setSelectedContactCard}
          handleContactCardInsert={handleContactCardInsert}
        />
      )
    },
    {
      type: 'alert',
      icon: <AlertOctagon className="h-5 w-5" />,
      label: 'Edit STOP message',
      onSelect: onAlertClick
    }
  ];

  return [mediaGroup, embedGroup, utilityGroup];
}
